import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  FaRobot,
  FaPhone,
  FaCheck,
  FaEnvelope,
  FaUserCircle,
  FaClipboardList,
  FaExclamationTriangle,
} from "react-icons/fa";
import ApiEndPoints from "../../../../ServicesCalls/ApiEndPoints";
import {
  formatStatusBadge,
  formatMobileNumber,
  getApiTokenFromLocalstorage,
} from "../../../reusable-components/PageFormats";

const CompletedCallsPatientInfoCard = ({ appointment }) => {
  const [patientLogInfo, setPatientLogInfo] = useState(null);
  const [patientInfoErr, setPatientInfoErr] = useState(null);
  const [patientLogStats, setPatientLogStats] = useState(null);
  const [isPatientInfoLoading, setIsPatientInfoLoading] = useState(true);

  const patientLogStatsAgentsInfo = useMemo(
    () => ({
      A1: {
        icon: (
          <FaClipboardList className="flex-shrink-0 mt-1 mr-4 text-gray-700 w-7 h-7" />
        ),
        iconLabel: "Data Loader",
        iconDescription: "Extracts patient details from the system",
      },
      A2: {
        icon: (
          <FaPhone className="flex-shrink-0 mt-1 mr-4 text-gray-700 w-7 h-7" />
        ),
        iconLabel: "Calling",
        iconDescription: "Handles outgoing calls to patients",
      },
      A3: {
        icon: (
          <FaEnvelope className="flex-shrink-0 mt-1 mr-4 text-gray-700 w-7 h-7" />
        ),
        iconLabel: "Voice Mail",
        iconDescription: "Sends voice messages and follow-up emails",
      },
      A4: {
        icon: (
          <FaCheck className="flex-shrink-0 mt-1 mr-4 text-gray-700 w-7 h-7" />
        ),
        iconLabel: "Response",
        iconDescription: "Captures and verifies patient responses",
      },
      A5: {
        icon: (
          <FaClipboardList className="flex-shrink-0 mt-1 mr-4 text-gray-700 w-7 h-7" />
        ),
        iconLabel: "Record & Transcript",
        iconDescription: "Maintains call recordings and transcripts",
        sub: "Call Status:CurrentAgentStatus, Call Start Time:CallStartTime, Call End Time:CallEndTime",
      },
      A6: {
        icon: (
          <FaRobot className="flex-shrink-0 mt-1 mr-4 text-gray-700 w-7 h-7" />
        ),
        iconLabel: "AI Summary",
        iconDescription: "Summarizes calls using AI insights",
      },
      A7: {
        icon: (
          <FaExclamationTriangle className="flex-shrink-0 mt-1 mr-4 text-gray-700 w-7 h-7" />
        ),
        iconLabel: "Escalation",
        iconDescription: "Escalates issues to the care team",
      },
      A8: {
        icon: (
          <FaEnvelope className="flex-shrink-0 mt-1 mr-4 text-gray-700 w-7 h-7" />
        ),
        iconLabel: "SMS",
        iconDescription: "Notifies patients via SMS alerts",
        sub: "SMS Status:SmsSentTime, Email Status:EmailStatus",
      },
    }),
    []
  );

  const fetchPatientInfo = useCallback(async () => {
    try {
      setPatientInfoErr(null);
      const requestBody = {
        PatientID: appointment?.PatientID,
      };
      const requestHeaders = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getApiTokenFromLocalstorage()}`,
        },
        body: JSON.stringify(requestBody),
      };
      const response = await fetch(
        ApiEndPoints.postAdminSelectedCompletedCall(),
        requestHeaders
      );
      const data = await response.json();
      const dataMsg = data?.message;

      if (!response.ok)
        throw new Error(dataMsg || "Failed to Fetch AgentLogs API");

      const dataPatient = data?.PatientDetails;
      const dataAgentRes = data?.AgentResponse;

      const arrDataRes1 =
        data && dataPatient
          ? Array.isArray(dataPatient)
            ? dataPatient
            : [dataPatient]
          : null;
      const arrDataRes2 =
        data && dataAgentRes
          ? Array.isArray(dataAgentRes)
            ? dataAgentRes
            : [dataAgentRes]
          : null;

      setPatientLogInfo(arrDataRes1);
      setPatientLogStats(
        arrDataRes2 &&
          [...arrDataRes2].sort((a, b) =>
            a?.CurrentAgentAt.localeCompare(b?.CurrentAgentAt, undefined, {
              numeric: true,
            })
          )
      );
    } catch (err) {
      const errorMsg = err.message;
      console.error(errorMsg);
      setPatientInfoErr(errorMsg);
      setPatientLogInfo(null);
      setPatientLogStats(null);
      return;
    } finally {
      setIsPatientInfoLoading(false);
    }
  }, [appointment]);

  useEffect(() => {
    if (appointment?.PatientID) {
      setIsPatientInfoLoading(true); // Show loader on every change
      fetchPatientInfo();
    }
  }, [appointment, fetchPatientInfo]);

  return (
    <>
      {isPatientInfoLoading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 pr-2 mr-3 border-t-2 border-blue-800 rounded-full animate-spin"></div>
          <span className="py-8 text-center text-blue-500">
            Loading Patient Logs Data...
          </span>
        </div>
      ) : patientInfoErr ? (
        <div className="py-8 text-center text-red-600">{patientInfoErr}</div>
      ) : (
        <div className="flex p-1">
          {/* Right Side Info */}
          <div className="w-full">
            {/* Patient Info Section */}
            {patientLogInfo &&
              patientLogInfo.map((patient, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 bg-blue-100 rounded-lg shadow"
                >
                  <FaUserCircle size={75} />
                  <div className="flex flex-col w-full">
                    <div className="text-lg font-bold">
                      {patient?.PatientName}
                    </div>
                    <div className="text-gray-700">
                      Age: {patient?.PatAge}, ID:{patient?.PatientID}, Mobile:{" "}
                      {formatMobileNumber(patient?.PatientMobile)}
                    </div>
                  </div>
                </div>
              ))}

            {/* Patient Log Stats Section */}
            {patientLogStats &&
              patientLogStats.map((patLog, index) => (
                <div
                  key={index}
                  className="flex items-start justify-between py-3 border-b"
                >
                  {/* Call Details (Left Column) */}
                  <div className="flex items-start w-1/4">
                    {patientLogStatsAgentsInfo[patLog?.CurrentAgentAt]?.icon}
                    <div>
                      <div className="text-base font-bold text-sky-700">
                        {patLog?.CurrentAgentAt}
                      </div>
                      <div className="text-sm text-gray-500">
                        {
                          patientLogStatsAgentsInfo[patLog?.CurrentAgentAt]
                            ?.iconDescription
                        }
                      </div>
                    </div>
                  </div>

                  {/* Step Details + Progress Bar (Middle Column) */}
                  <div className="flex flex-col w-2/4 px-2">
                    <div className="flex justify-between">
                      {/* Step Details */}
                      <div>
                        <div className="font-semibold text-md">
                          {patientLogStatsAgentsInfo[patLog?.CurrentAgentAt]
                            ?.iconLabel || patLog?.CurrentAgentAt}
                        </div>
                        <div className="text-sm text-gray-500">
                          {patLog?.CreatedDate}
                        </div>
                        <div className="text-sm text-gray-500">
                          {patLog?.CreatedTime}
                        </div>
                      </div>
                      {/* Progress Bar */}
                      <div className="flex flex-col justify-center w-3/5">
                        <div className="relative w-full h-1 bg-gray-200">
                          <div className="absolute inset-0 bg-green-500"></div>
                          <div className="absolute left-0 w-3 h-3 -ml-1 bg-green-500 rounded-full"></div>
                          <div className="absolute right-0 w-3 h-3 -mr-1 bg-green-500 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                    {/* Sub line below both */}
                    {patientLogStatsAgentsInfo[patLog?.CurrentAgentAt]?.sub && (
                      <div className="flex items-center justify-center gap-4 pt-1 text-xs text-gray-600">
                        {patientLogStatsAgentsInfo[patLog?.CurrentAgentAt]?.sub
                          .split(",")
                          .map((text, idx) => {
                            const [label, key] = text.split(":");
                            let apiValue;
                            if (key?.trim() === "CurrentAgentStatus") {
                              apiValue = formatStatusBadge(patLog?.CurrentAgentStatus);
                            } else {
                              apiValue = patLog?.[key?.trim()] ?? "N/A";
                            }
                            return (
                              <span key={idx} className="whitespace-nowrap">
                                <span className="font-bold">
                                  {label?.trim()}:
                                </span>{" "}
                                <span>{apiValue}</span>
                              </span>
                            );
                          })}
                      </div>
                    )}
                  </div>

                  {/* Status (Right Column) */}
                  <div className="w-1/4 text-center">
                    <div className="font-medium">
                      {formatStatusBadge(patLog?.CurrentAgentStatus)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {patLog?.UpdatedDate}
                    </div>
                    <div className="text-sm text-gray-500">
                      {patLog.UpdatedTime}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </>
  );
};

export default CompletedCallsPatientInfoCard;
